/* Reverie Night: small progressive enhancements. Everything works without this file. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // The headlight ignition plays once per visit (and again on reload); remember that it ran.
  if (document.querySelector('.hero--ignition')) {
    try { sessionStorage.setItem('reverie-ignited', '1'); } catch (e) { /* storage blocked: it simply plays again */ }
  }

  // Homepage: the header floats over the opening photo while it is in view
  const hero = document.querySelector('[data-hero]');
  const heroVideo = () => document.querySelector('[data-hero-video]');
  let heroVisible = true;
  if (hero && 'IntersectionObserver' in window) {
    const header = document.querySelector('.site-header');
    const observer = new IntersectionObserver(([entry]) => {
      heroVisible = entry.isIntersecting;
      document.documentElement.classList.toggle('hero-in-view', entry.isIntersecting);
      // the film only runs while you can see it
      const video = heroVideo();
      if (!video || !video.closest('.video-on') || video.dataset.userPaused) return;
      if (entry.isIntersecting) video.play().catch(() => {}); else video.pause();
    }, { rootMargin: `-${header ? header.offsetHeight : 64}px 0px 0px 0px`, threshold: 0.12 });
    observer.observe(hero);
  } else {
    document.documentElement.classList.remove('hero-in-view');
  }

  // Product page: each photo comes on once it has loaded and decoded (base.css fades it in)
  document.querySelectorAll('.product-media li:not(.scan-stage) img').forEach((img) => {
    const on = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth) { on(); return; }
    img.addEventListener('load', () => { (img.decode ? img.decode() : Promise.resolve()).catch(() => {}).then(on); }, { once: true });
    img.addEventListener('error', on, { once: true });
  });

  // Pause / play the opening film
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-video-toggle]');
    if (!toggle) return;
    const video = heroVideo();
    if (!video) return;
    const pause = !video.paused;
    if (pause) { video.pause(); video.dataset.userPaused = '1'; } else { delete video.dataset.userPaused; if (heroVisible) video.play().catch(() => {}); }
    toggle.classList.toggle('is-paused', pause);
    toggle.querySelector('[data-video-toggle-text]').textContent = pause ? 'Speel film af' : 'Pauzeer film';
  });

  // Photo strip: a position bar and previous / next buttons
  document.querySelectorAll('[data-gallery-controls]').forEach((controls) => {
    const strip = controls.parentElement.querySelector('.gallery-strip');
    if (!strip) return;
    const bar = controls.querySelector('[data-gallery-progress]');
    const [prev, next] = controls.querySelectorAll('[data-gallery-step]');
    const update = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      controls.hidden = max < 8;
      bar.style.setProperty('--w', (strip.clientWidth / strip.scrollWidth).toFixed(4));
      bar.style.setProperty('--o', (strip.scrollLeft / strip.scrollWidth).toFixed(4));
      prev.disabled = strip.scrollLeft < 8;
      next.disabled = strip.scrollLeft > max - 8;
    };
    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-gallery-step]');
      if (!button) return;
      const item = strip.querySelector('li');
      const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : strip.clientWidth * 0.8;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      strip.scrollBy({ left: step * Number(button.dataset.galleryStep), behavior: smooth ? 'smooth' : 'auto' });
    });
    let frame;
    strip.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  // Theme editor: a re-rendered opening section gets its film started again
  document.addEventListener('shopify:section:load', (event) => {
    const video = event.target.querySelector('[data-hero-video]');
    if (video && window.reverieHeroVideo) window.reverieHeroVideo(video);
  });

  // Quantity steppers
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.closest('[data-qty]').querySelector('input');
    const min = Number(input.min || 0);
    const max = Number(input.max || 99);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep)));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart page: quantity changes update the cart right away
  let cartTimer;
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-auto-update]');
    if (!input) return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(() => input.form.submit(), 450);
  });

  // Variant choice: keep price, button and URL in step with the selected variant
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const section = select.closest('section');
    const priceNow = section && section.querySelector('[data-price-area] .price-now');
    if (priceNow && option.dataset.price) priceNow.textContent = option.dataset.price;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    window.history.replaceState({}, '', url);
  });

  // Add to cart without leaving the page; falls back to the normal form post on any error
  const setCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.classList.toggle('is-empty', count === 0);
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
      const link = badge.closest('a');
      if (link) link.setAttribute('aria-label', `Winkelwagen, ${count} ${count === 1 ? 'artikel' : 'artikelen'}`);
    });
  };

  // prices the way the shop writes them (Instellingen > Valuta), e.g. €30,00
  const money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    const fixed = (decimals, thousands, mark) => {
      const [whole, part] = n.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? mark + part : '');
    };
    const forms = {
      amount: () => fixed(2, ',', '.'),
      amount_no_decimals: () => fixed(0, ',', '.'),
      amount_with_comma_separator: () => fixed(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fixed(0, '.', ','),
      amount_with_space_separator: () => fixed(2, ' ', ','),
      amount_with_apostrophe_separator: () => fixed(2, "'", '.'),
    };
    const format = window.reverieMoneyFormat || '€{{amount_with_comma_separator}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => (forms[key] || forms.amount_with_comma_separator)());
  };

  // The confirmation after adding: what went in, what the cart holds now, and the way to it. It goes by itself after
  // 10 s, shown by a light along its top edge, round the corners and a little way down, running empty; pointing at
  // it, or tabbing to its buttons, holds the time.
  // Before that: the cross, "Verder winkelen", Escape or a click next to it.
  // It comes on screen once and complete: the add waits (briefly) for the cart's new total and the product shot,
  // so nothing grows or pops in while it slides in, and the clock only starts once it has settled.
  const POP_TIME = 10000;
  const POP_ENTER = 400;
  let pop = null;
  let popLeave = 0;
  let popStart = 0;
  let popReturn = null;
  let popLeft = 0;
  let popHeld = false;
  let popFrame = 0;
  let popLen = 0;
  // the light's path: the card's own border (its outer edge, its radius) from just down the left side, round the
  // top-left corner, along the top and round the top-right corner to just down the right side
  const popTrace = () => {
    const svg = pop.querySelector('.cart-pop-timer');
    const path = svg.querySelector('path');
    const w = pop.offsetWidth;
    const r = parseFloat(getComputedStyle(pop).borderTopLeftRadius) || 12;
    const a = r - 0.5; // the middle of the 1px border
    const down = r + 6;
    svg.setAttribute('viewBox', `0 0 ${w} ${down}`);
    svg.setAttribute('width', w);
    svg.setAttribute('height', down);
    path.setAttribute('d', `M0.5 ${down}V${r}A${a} ${a} 0 0 1 ${r} 0.5H${w - r}A${a} ${a} 0 0 1 ${w - 0.5} ${r}V${down}`);
    popLen = path.getTotalLength();
    path.style.strokeDasharray = `${popLen} ${popLen}`;
  };
  const popDraw = () => {
    const f = Math.max(popLeft, 0) / POP_TIME;
    pop.querySelector('.cart-pop-timer path').style.strokeDashoffset = `${popLen * (1 - f)}`;
  };
  const popTick = (() => {
    let last = 0;
    const tick = (now) => {
      if (!pop || pop.hidden) { popFrame = 0; return; }
      const dt = last ? Math.min(now - last, 100) : 0; // a tab in the background does not count down
      last = now;
      if (!popHeld && now - popStart > POP_ENTER) popLeft -= dt;
      popDraw();
      if (popLeft <= 0) { popFrame = 0; last = 0; popClose(pop.contains(document.activeElement)); return; }
      popFrame = requestAnimationFrame(tick);
    };
    return () => { cancelAnimationFrame(popFrame); last = 0; popFrame = requestAnimationFrame(tick); };
  })();
  const popClose = (restoreFocus) => {
    if (!pop || pop.hidden || pop.classList.contains('is-leaving')) return;
    cancelAnimationFrame(popFrame);
    pop.classList.add('is-leaving');
    clearTimeout(popLeave);
    popLeave = setTimeout(() => { pop.hidden = true; pop.classList.remove('is-leaving'); }, 200);
    if (restoreFocus && popReturn && document.contains(popReturn)) popReturn.focus({ preventScroll: true });
  };
  const popBuild = () => {
    pop = document.createElement('div');
    pop.className = 'cart-pop';
    pop.hidden = true;
    pop.tabIndex = -1;
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-labelledby', 'cart-pop-title');
    pop.innerHTML = `
      <svg class="cart-pop-timer" aria-hidden="true" focusable="false"><path/></svg>
      <button type="button" class="cart-pop-close" data-pop-close aria-label="Sluiten"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
      <p class="cart-pop-title" id="cart-pop-title"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>Toegevoegd aan je winkelwagen</p>
      <div class="cart-pop-item">
        <span class="cart-pop-shot"><img alt="" width="56" height="70" decoding="async"></span>
        <span class="cart-pop-name"></span>
        <span class="cart-pop-meta"></span>
        <span class="cart-pop-price"></span>
      </div>
      <p class="cart-pop-total"></p>
      <div class="cart-pop-actions">
        <a class="button button--light" href="${root}cart">Naar winkelwagen</a>
        <button type="button" class="button button--ghost" data-pop-close>Verder winkelen</button>
      </div>`;
    document.body.append(pop);
    pop.addEventListener('click', (event) => { if (event.target.closest('[data-pop-close]')) popClose(true); });
    pop.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.stopPropagation(); popClose(true); } });
    document.addEventListener('click', (event) => {
      if (!pop || pop.hidden || pop.contains(event.target)) return;
      if (event.target.closest('[data-product-form] [name="add"]')) return; // adding again: the card stays and updates
      popClose(false);
    });
    const keyHold = () => pop.contains(document.activeElement) && document.activeElement !== pop;
    pop.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') popHeld = true; });
    pop.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') popHeld = keyHold(); });
    pop.addEventListener('focusin', () => { if (keyHold()) popHeld = true; });
    pop.addEventListener('focusout', () => { requestAnimationFrame(() => { if (!pop.matches(':hover')) popHeld = keyHold(); }); });
    window.addEventListener('resize', () => { if (!pop.hidden) { popTrace(); popDraw(); } });
  };
  // the product shot, small, loaded and decoded before the card shows it
  const popShot = (item) => {
    const src = (item.featured_image && item.featured_image.url) || item.image;
    if (!src) return { url: '', ready: Promise.resolve() };
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', '160');
    const image = new Image();
    image.src = url.href;
    const ready = image.decode ? image.decode().catch(() => {}) : new Promise((done) => { image.onload = image.onerror = done; });
    return { url: url.href, ready };
  };
  const popShow = (item, quantity, button, shot, cart) => {
    if (!pop) popBuild();
    popReturn = button;
    const img = pop.querySelector('.cart-pop-shot img');
    if (shot.url) {
      img.src = shot.url;
      img.parentElement.hidden = false;
    } else img.parentElement.hidden = true;
    pop.querySelector('.cart-pop-name').textContent = item.product_title || item.title || '';
    const variant = item.variant_title && !/^default title$/i.test(item.variant_title) ? `${item.variant_title}, ` : '';
    pop.querySelector('.cart-pop-meta').textContent = `${variant}${quantity} ${quantity === 1 ? 'stuk' : 'stuks'}`;
    pop.querySelector('.cart-pop-price').textContent = money((item.final_price ?? item.price) * quantity);
    pop.querySelector('.cart-pop-total').textContent = '';
    const open = !pop.hidden && !pop.classList.contains('is-leaving');
    // under the header while it is in view (on the homepage it comes back for this, with the cart's new count),
    // otherwise at the top of the screen
    const header = document.querySelector('.site-header');
    const pinned = header && (getComputedStyle(header).position === 'fixed' || getComputedStyle(header.parentElement).position === 'sticky');
    pop.style.top = !header || pinned ? '' : `${Math.max(10, Math.round(header.getBoundingClientRect().bottom) + 8)}px`;
    html.classList.remove('header-hidden');
    clearTimeout(popLeave);
    pop.classList.remove('is-leaving');
    pop.hidden = false;
    if (cart) popTotal(cart);
    popLeft = POP_TIME;
    popStart = open ? 0 : performance.now(); // already open (added again): the clock simply starts over
    popHeld = pop.matches(':hover');
    popTrace();
    popDraw();
    popTick();
    setTimeout(() => { if (!pop.hidden) pop.focus({ preventScroll: true }); }, open ? 0 : POP_ENTER); // after the slide-in
  };
  const popTotal = (cart) => {
    if (!pop || pop.hidden) return;
    const count = cart.item_count;
    pop.querySelector('.cart-pop-total').textContent = `Totaal ${count} ${count === 1 ? 'artikel' : 'artikelen'}, ${money(cart.total_price)}`;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    const label = button.textContent;
    let status = form.querySelector('.cart-added');
    if (!status) {
      status = document.createElement('p');
      status.className = 'cart-added';
      status.setAttribute('role', 'status');
      const row = form.querySelector('.buy-row');
      if (row) row.after(status); else form.append(status);
    }
    button.disabled = true;
    button.classList.add('is-busy');
    status.classList.remove('form-status--error');
    status.textContent = '';
    let response;
    let data = {};
    try {
      response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      data = await response.json().catch(() => ({}));
    } catch (error) {
      form.submit(); // no connection to the AJAX endpoint: let the browser post the form instead
      return;
    }
    const done = () => {
      button.disabled = false;
      button.classList.remove('is-busy');
      button.textContent = label;
    };
    if (!response.ok) {
      done();
      status.classList.add('form-status--error');
      status.textContent = data.description || data.message || 'Toevoegen lukte niet. Probeer het nog eens.';
      return;
    }
    const added = Number((form.querySelector('[name="quantity"]') || {}).value) || 1;
    const item = (data.items && data.items[0]) || data;
    // the new total and the shot, together; the button stays busy meanwhile. After 1.2 s the card goes without them
    // (the total then follows, the count catches up on the next page at the latest).
    const shot = popShot(item);
    const cartLoad = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    const cart = await Promise.race([
      Promise.all([cartLoad, shot.ready]).then(([loaded]) => loaded),
      new Promise((resolve) => setTimeout(resolve, 1200, undefined)),
    ]);
    done();
    popShow(item, added, button, shot, cart);
    const counted = cart === undefined ? await cartLoad : cart;
    if (counted) {
      setCount(counted.item_count);
      if (cart === undefined) popTotal(counted);
    }
  });

  // ---------- Motion ----------
  // The layout sets .motion-ok before the first paint when animation is welcome; this flag tells it we started.
  const html = document.documentElement;
  window.__reverieMotion = true;
  const motion = html.classList.contains('motion-ok');
  const menu = document.querySelector('[data-menu]');

  // HUD text decodes like a readout: random glyphs settle into the real letters
  const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/<>+-';
  const decode = (el, duration = 650) => {
    if (!motion || !el || el.dataset.decoded) return;
    el.dataset.decoded = '1';
    let target = el;
    if (!el.closest('[aria-hidden="true"]')) {
      // screen readers get the real text; the animated copy is hidden from them
      const real = document.createElement('span');
      real.className = 'visually-hidden';
      real.textContent = el.textContent.replace(/\s+/g, ' ').trim();
      target = document.createElement('span');
      target.setAttribute('aria-hidden', 'true');
      while (el.firstChild) target.appendChild(el.firstChild);
      el.append(real, target);
    }
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let total = 0;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      nodes.push([node, node.nodeValue, total]);
      total += node.nodeValue.length;
    }
    if (!total) return;
    const TRAIL = 5;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const front = Math.floor(p * (total + TRAIL));
      nodes.forEach(([node, text, offset]) => {
        let out = '';
        for (let i = 0; i < text.length; i += 1) {
          const at = offset + i;
          const ch = text[i];
          if (at < front - TRAIL || /\s/.test(ch)) out += ch;
          else if (at < front) out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
          else out += ' '; // keeps the width, so nothing shifts
        }
        node.nodeValue = out;
      });
      if (p < 1) requestAnimationFrame(tick);
      else nodes.forEach(([node, text]) => { node.nodeValue = text; });
    };
    requestAnimationFrame(tick);
  };

  let glideToDrop = null; // set below when motion is welcome: the homepage glide from the film to the drop
  if (motion) {
    // stagger siblings that reveal together
    const counts = new Map();
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      const i = counts.get(el.parentElement) || 0;
      counts.set(el.parentElement, i + 1);
      el.style.setProperty('--i', i);
    });

    const reveal = (el) => {
      el.classList.add('is-in');
      const delay = Math.min(Number(el.style.getPropertyValue('--i')) || 0, 8) * 90 + 120;
      const targets = el.matches('[data-decode]') ? [el] : [...el.querySelectorAll('[data-decode]')];
      targets.forEach((t) => setTimeout(() => decode(t), delay));
    };
    const revealer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealer.unobserve(entry.target);
        reveal(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    // the product photo: one scan from top to bottom, each callout locks on as the scan passes it
    const goLive = (photo) => {
      if (photo.classList.contains('is-live')) return;
      photo.classList.add('is-live');
      photo.querySelectorAll('.callout').forEach((callout) => {
        const y = parseFloat(callout.style.getPropertyValue('--yn')) || 50;
        setTimeout(() => decode(callout.querySelector('.callout-label'), 600), 200 + y * 19 + 600);
      });
    };

    // The drop under the opening film (sections/featured-product.liquid). When it fills the screen it plays once:
    // assets/drop-glass.js puts the coating on the wet glass (when it runs), and the name and the buy column
    // arrive as the glass clears. On phones the name is in the first screen; the rest arrives as you scroll to it.
    const dropSection = document.querySelector('[data-drop]');
    const dropCopy = dropSection && dropSection.querySelector('.drop-copy');
    if (dropCopy) {
      const head = [...dropSection.querySelectorAll('.drop-head [data-reveal]')];
      const rest = [...dropCopy.querySelectorAll('[data-reveal]')].filter((el) => !head.includes(el));
      const wide = window.matchMedia('(min-width: 1000px) and (min-aspect-ratio: 5/4)');
      if (!wide.matches) rest.forEach((el) => revealer.observe(el));
      let state = '';
      const tell = (name) => {
        state = name;
        dropSection.dataset.dropState = name; // for the glass script, if it starts later
        dropSection.dataset.dropAt = String(performance.now());
        dropSection.dispatchEvent(new CustomEvent(`drop:${name}`));
      };

      // While the drop plays, the page holds: you can't scroll on past it until the bottle has sprayed both pulls and
      // the mist is down on the glass (drop-glass.js says when: 'drop:sprayed', about 2.3 s), and the name is in;
      // the fog lifting and the beads running off go on while you scroll. Without the glass it holds 2.2 s.
      // Back up is held too (the section can only settle the last bit into place), and it never holds longer than 3.2 s.
      // Wheel, touch and keys are stopped; anything else that moves the page (a scrollbar, a fling) is put back.
      const holdScroll = () => {
        const root = document.documentElement;
        const begin = performance.now();
        const top = dropSection.getBoundingClientRect().top + window.scrollY;
        const limit = Math.max(Math.round(top), Math.round(window.scrollY)); // the section filling the screen
        let floor = Math.min(Math.round(window.scrollY), limit); // where it started; moving on down raises it
        const glass = dropSection.classList.contains('glass-on');
        let touchY = null;
        let timer = 0;
        const typing = (el) => el && el.closest && el.closest('input, textarea, select, [contenteditable]');
        const onWheel = (event) => {
          if (event.ctrlKey || !event.cancelable) return;
          if (event.deltaY < 0 || (event.deltaY > 0 && window.scrollY >= limit - 1)) event.preventDefault();
        };
        const onTouchStart = (event) => { touchY = event.touches.length === 1 ? event.touches[0].clientY : null; };
        const onTouchMove = (event) => {
          if (touchY === null || event.touches.length !== 1) return;
          const y = event.touches[0].clientY;
          const dy = y - touchY;
          touchY = y;
          if (!dy || !event.cancelable) return;
          // the finger goes down: the page would go back up, always held; up: on down, held once the section fills the screen
          if (dy > 0 || window.scrollY >= limit - 1) event.preventDefault();
        };
        const onKey = (event) => {
          const keys = ['ArrowDown', 'PageDown', 'End', 'ArrowUp', 'PageUp', 'Home', ' '];
          if (!keys.includes(event.key) || typing(event.target) || event.altKey || event.ctrlKey || event.metaKey) return;
          event.preventDefault();
        };
        // a scrollbar dragged, a fling already under way: back between where it started and where the section fills the screen
        const onScroll = () => {
          const y = window.scrollY;
          if (y > limit + 2) window.scrollTo(0, limit);
          else if (y < floor - 2) window.scrollTo(0, floor);
          else floor = Math.max(floor, Math.round(y));
        };
        // tabbing on to something further down: the hold makes way at once
        const onFocus = (event) => { if (!dropSection.contains(event.target)) letGo(); };
        const wheelOptions = { passive: false };
        const touchOptions = { passive: false };
        const letGo = () => {
          clearTimeout(timer);
          root.classList.remove('drop-hold');
          window.removeEventListener('wheel', onWheel, wheelOptions);
          window.removeEventListener('touchstart', onTouchStart);
          window.removeEventListener('touchmove', onTouchMove, touchOptions);
          window.removeEventListener('keydown', onKey);
          window.removeEventListener('scroll', onScroll);
          document.removeEventListener('focusin', onFocus);
          dropSection.removeEventListener('drop:sprayed', onSprayed);
        };
        // the spray is down: let go a moment later, once the name is in too (at least 2.4 s after the start)
        const onSprayed = () => {
          clearTimeout(timer);
          timer = setTimeout(letGo, Math.max(250, 2400 - (performance.now() - begin)));
        };
        root.classList.add('drop-hold');
        window.addEventListener('wheel', onWheel, wheelOptions);
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, touchOptions);
        window.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('focusin', onFocus);
        dropSection.addEventListener('drop:sprayed', onSprayed, { once: true });
        timer = setTimeout(letGo, glass ? 3200 : 2200);
      };

      const play = () => {
        tell('play');
        holdScroll();
        // the glass waits a moment wet, then the bottle sprays it twice and the water runs off
        setTimeout(() => head.filter((el) => !el.matches('.drop-title')).forEach(reveal), 250);
        setTimeout(() => head.filter((el) => el.matches('.drop-title')).forEach(reveal), 1600);
        if (wide.matches) setTimeout(() => rest.forEach(reveal), 2000);
      };
      // arriving further down (a reload, a link) or tabbing into it: everything is simply there
      const skip = () => {
        tell('skip');
        head.forEach(reveal);
        if (wide.matches) rest.forEach(reveal);
      };
      let dropFrame = 0;
      const check = () => {
        dropFrame = 0;
        if (state) return;
        const r = dropSection.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.top <= vh * 0.12 && r.top > -vh * 0.5) play();
        else if (r.top <= -vh * 0.5) skip();
      };
      check();
      window.addEventListener('scroll', () => { if (!dropFrame) dropFrame = requestAnimationFrame(check); }, { passive: true });
      dropCopy.addEventListener('focusin', () => { if (!state) skip(); });
    }

    // Community reel (sections/gallery.liquid): two lanes of photos slide past in opposite directions while the
    // page scrolls by, like traffic at night. Each lane gets copies of its photos after it, so it never runs out.
    document.querySelectorAll('[data-reel]').forEach((reel) => {
      const rows = [...reel.querySelectorAll('[data-reel-row]')];
      if (!rows.length) return;
      const SPEED = 0.38; // how far the photos move for every pixel the page scrolls
      let periods = [];
      let reelFrame = 0;
      const build = () => {
        reel.classList.add('is-moving');
        const vw = window.innerWidth;
        periods = rows.map((row) => {
          row.querySelectorAll('[data-reel-copy]').forEach((el) => el.remove());
          row.style.transform = '';
          const originals = [...row.children];
          if (!originals.length) return 0;
          const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
          const period = row.scrollWidth + gap; // one set of photos and the gap to the next set
          // enough copies that a full screen of photos follows wherever the lane has moved to
          const copies = Math.max(1, Math.ceil(vw / period));
          for (let c = 0; c < copies; c += 1) {
            originals.forEach((item) => {
              const copy = item.cloneNode(true);
              copy.setAttribute('aria-hidden', 'true');
              copy.setAttribute('data-reel-copy', '');
              copy.removeAttribute('data-shopify-editor-block');
              row.appendChild(copy);
            });
          }
          return period;
        });
      };
      const update = () => {
        reelFrame = 0;
        const box = reel.getBoundingClientRect();
        const vh = window.innerHeight;
        if (box.bottom < -200 || box.top > vh + 200) return;
        const travel = (vh - box.top) * SPEED;
        rows.forEach((row, i) => {
          const period = periods[i];
          if (!period) return;
          const off = ((travel % period) + period) % period;
          // the top lane runs to the left, the bottom lane to the right
          const x = i % 2 === 0 ? -off : off - period;
          row.style.transform = `translate3d(${x.toFixed(1)}px, 0, 0)`;
        });
      };
      build();
      update();
      window.addEventListener('scroll', () => { if (!reelFrame) reelFrame = requestAnimationFrame(update); }, { passive: true });
      let lastWidth = window.innerWidth;
      window.addEventListener('resize', () => {
        // phones fire resize when the address bar slides; only a new width needs new copies
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        cancelAnimationFrame(reelFrame);
        reelFrame = requestAnimationFrame(() => { build(); update(); });
      });
    });

    // Homepage: the first scroll down from the opening film glides to the drop until it fills the screen.
    // It happens once per page load (a refresh gives it again); after that the page scrolls as usual everywhere,
    // and these listeners come off so scrolling stays fully native.
    const film = document.querySelector('[data-hero]');
    const drop = document.querySelector('[data-drop]');
    if (film && drop) {
      let snapping = false;
      let settling = false; // after the glide: swallow the trackpad's leftover momentum
      let lastWheel = 0;
      let glideFrame = 0;
      let touch = null;
      let release = () => {};
      const pageTop = (el) => el.getBoundingClientRect().top + window.scrollY;
      // where a scroll down from the film glides to, or null: then it is an ordinary scroll
      const target = () => {
        const top = pageTop(drop);
        // only when the drop follows straight after the film
        if (pageTop(film) > 4 || Math.abs(pageTop(film) + film.offsetHeight - top) > 4) return null;
        return window.scrollY < top - 2 ? top : null;
      };
      // the glide is over once the momentum has died down and no finger is still on the glass
      const settle = () => {
        if (performance.now() - lastWheel < 200 || (touch && touch.held)) { setTimeout(settle, 120); return; }
        settling = false;
        release();
      };
      const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2);
      const glide = (to) => {
        const from = window.scrollY;
        const distance = to - from;
        const duration = Math.min(1100, Math.max(650, Math.abs(distance) * 0.9));
        const begin = performance.now();
        snapping = true;
        const step = (time) => {
          const x = Math.min(1, (time - begin) / duration);
          window.scrollTo(0, from + distance * ease(x));
          if (x < 1) { glideFrame = requestAnimationFrame(step); return; }
          snapping = false;
          settling = true;
          settle();
        };
        glideFrame = requestAnimationFrame(step);
      };

      const onWheel = (event) => {
        if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        lastWheel = performance.now();
        if (snapping || settling) { event.preventDefault(); return; }
        if (event.deltaY <= 0) return;
        const to = target();
        if (to === null) return;
        event.preventDefault();
        glide(to);
      };
      const onTouchStart = (event) => {
        touch = event.touches.length === 1 ? { y: event.touches[0].clientY, decided: false, held: false } : null;
      };
      const onTouchMove = (event) => {
        if (snapping || (touch && touch.held)) { if (event.cancelable) event.preventDefault(); return; }
        if (!touch || touch.decided || event.touches.length !== 1) return;
        const dy = touch.y - event.touches[0].clientY; // above 0: the finger goes up, the page would go down
        if (!dy) return;
        touch.decided = true;
        if (dy < 0) return;
        const to = target();
        if (to === null || !event.cancelable) return;
        touch.held = true;
        event.preventDefault();
        glide(to);
      };
      const onTouchEnd = () => { touch = null; };
      const onKey = (event) => {
        const down = event.key === 'ArrowDown' || event.key === 'PageDown' || (event.key === ' ' && !event.shiftKey);
        if (!down && event.key !== 'ArrowUp' && event.key !== 'PageUp' && event.key !== ' ') return;
        if (snapping) { event.preventDefault(); return; }
        if (!down || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
        const el = event.target;
        if (el && el.closest && el.closest('input, textarea, select, [contenteditable]')) return;
        if (event.key === ' ' && el !== document.body) return;
        const to = target();
        if (to === null) return;
        event.preventDefault();
        glide(to);
      };
      const wheelOptions = { passive: false };
      const touchOptions = { passive: false };
      window.addEventListener('wheel', onWheel, wheelOptions);
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, touchOptions);
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('touchcancel', onTouchEnd, { passive: true });
      window.addEventListener('keydown', onKey);
      // "Ontdek meer" in the film glides down the same way (wired up below, outside the motion block)
      glideToDrop = (to) => { if (!snapping) glide(to); };
      release = () => {
        cancelAnimationFrame(glideFrame);
        window.removeEventListener('wheel', onWheel, wheelOptions);
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove, touchOptions);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('touchcancel', onTouchEnd);
        window.removeEventListener('keydown', onKey);
      };
    }

    // Over Reverie (sections/about.liquid): the story lights up word by word as it scrolls through the screen,
    // like headlights coming on; scrolled back up, the words go dark again behind you
    document.querySelectorAll('[data-ignite]').forEach((box) => {
      const words = [];
      const walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
      const texts = [];
      while (walker.nextNode()) if (walker.currentNode.nodeValue.trim()) texts.push(walker.currentNode);
      texts.forEach((node) => {
        const frag = document.createDocumentFragment();
        node.nodeValue.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          const span = document.createElement('span');
          span.className = 'ignite-word';
          span.textContent = part;
          words.push(span);
          frag.appendChild(span);
        });
        node.parentNode.replaceChild(frag, node);
      });
      if (!words.length) return;
      box.classList.add('is-igniting');
      let lit = 0;
      let frame = 0;
      const update = () => {
        frame = 0;
        const r = box.getBoundingClientRect();
        const vh = window.innerHeight;
        // none lit while its top is still low on the screen, all lit once its end has come up past the middle
        const p = Math.min(1, Math.max(0, (vh * 0.82 - r.top) / (r.height + vh * 0.32)));
        const n = Math.round(p * words.length);
        if (n === lit) return;
        const [a, b, on] = n > lit ? [lit, n, true] : [n, lit, false];
        for (let i = a; i < b; i += 1) words[i].classList.toggle('is-lit', on);
        lit = n;
      };
      update();
      window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', () => { if (!frame) frame = requestAnimationFrame(update); });
    });

    // the steps (sections/steps.liquid): on a computer the whole row comes on in one run once most of it is in view;
    // on a phone every row on its own as it scrolls in
    const stepsWide = window.matchMedia('(min-width: 900px)');
    // (it starts once the whole row is (nearly) in view, so the light is seen running all the way; on a screen too low
    // for that, once its top is a quarter of the way up)
    const stepsRun = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.intersectionRatio < 0.9 && entry.boundingClientRect.top > window.innerHeight * 0.25) return;
        stepsRun.unobserve(entry.target);
        entry.target.classList.add('is-in');
      });
    }, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) });

    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (el.closest('.drop-copy')) return; // the drop reveals its own
      // product page: what is in (or just under) the first screen comes in straight away, not when it is scrolled to
      if (el.closest('.product-info') && el.getBoundingClientRect().top < window.innerHeight * 1.2) {
        requestAnimationFrame(() => reveal(el));
        return;
      }
      if (el.matches('[data-reveal="steps"]')) {
        if (stepsWide.matches) stepsRun.observe(el);
        else el.querySelectorAll('.step').forEach((step) => revealer.observe(step));
        return;
      }
      // already on the first screen: in straight away. (Chrome counts a title's closed clip-path as "not visible",
      // so a page title at the top would otherwise never come in on a phone.)
      const r = el.getBoundingClientRect();
      if ((r.width || r.height) && r.bottom > 0 && r.top < window.innerHeight * 0.92) {
        requestAnimationFrame(() => reveal(el));
        return;
      }
      revealer.observe(el);
    });

    document.querySelectorAll('[data-scan]').forEach((photo) => {
      const scanner = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        scanner.disconnect();
        // when the photo itself is still being revealed (product page), scan once it is fully open
        setTimeout(() => goLive(photo), photo.closest('[data-reveal]') ? 250 : 0);
      }, { threshold: 0.45 });
      scanner.observe(photo);
    });

    // tilt the product photo towards the pointer; the callouts float above it
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.querySelectorAll('[data-tilt]').forEach((stage) => {
        let frame;
        const set = (x, y) => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            stage.style.setProperty('--mx', x.toFixed(3));
            stage.style.setProperty('--my', y.toFixed(3));
          });
        };
        stage.addEventListener('pointermove', (event) => {
          const box = stage.getBoundingClientRect();
          set(((event.clientX - box.left) / box.width) * 2 - 1, ((event.clientY - box.top) / box.height) * 2 - 1);
        });
        stage.addEventListener('pointerleave', () => set(0, 0));
      });
    }

    // header: out of the way while scrolling down, back when scrolling up
    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (Math.abs(delta) < 6) return;
      html.classList.toggle('header-hidden', delta > 0 && y > 320 && !(menu && menu.open));
      lastY = y;
    }, { passive: true });

    // a thin line along the top while the next page loads
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      html.classList.add('is-navigating');
    });
    window.addEventListener('pageshow', (event) => { if (event.persisted) html.classList.remove('is-navigating'); });
  }

  // "Ontdek meer" in the opening film (sections/hero.liquid): down to the drop, until it fills the screen.
  // Without motion it jumps there; on a page without the drop it goes to the product instead.
  document.querySelectorAll('[data-hero-more]').forEach((link) => {
    const drop = document.querySelector('[data-drop]');
    if (!drop) { link.href = link.dataset.fallback; return; }
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const to = Math.round(drop.getBoundingClientRect().top + window.scrollY);
      if (Math.abs(window.scrollY - to) < 2) return;
      if (glideToDrop) glideToDrop(to);
      else window.scrollTo(0, to);
    });
  });

  // Mobile menu: close on Escape or a tap outside
  if (menu) {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.open) {
        menu.open = false;
        menu.querySelector('summary').focus();
      }
    });
    document.addEventListener('click', (event) => {
      if (menu.open && !menu.contains(event.target)) menu.open = false;
    });
    // a page chosen in the menu: the menu closes at once, so the next page does not come in under an open menu
    menu.addEventListener('click', (event) => {
      const link = event.target.closest('.menu-panel a[href]');
      if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey) menu.open = false;
    });
  }

  // Homepage: everything above is set up; the opening may start (layout/theme.liquid waits for this)
  if (window.reverieGate) window.reverieGate.done('theme');
})();
